import { useForm } from "react-hook-form";
import useBrandTranslation from "../hooks/useBrandTranslation";
import Button from "./common/Button";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import { Textarea } from "./common/Textarea";
import { DialogBody, DialogFooter } from "./common/Dialog";
import { HealthInsuranceSelect } from "./HealthInsurance";

// Opciones de vínculo (clave i18n + default español). Reutilizadas por la tab
// de Familiares y por el selector "Operar con…".
export const RELATIONSHIP_OPTIONS = [
  { value: "child", labelKey: "app.dependents.relChild", labelDefault: "Hijo/a" },
  { value: "spouse", labelKey: "app.dependents.relSpouse", labelDefault: "Cónyuge" },
  { value: "parent", labelKey: "app.dependents.relParent", labelDefault: "Madre/Padre" },
  { value: "sibling", labelKey: "app.dependents.relSibling", labelDefault: "Hermano/a" },
  { value: "other", labelKey: "app.dependents.relOther", labelDefault: "Otro" },
];

// Mismas opciones de discapacidad que el perfil del paciente (ver Profile.js).
export const DEPENDENT_DISABILITY_OPTIONS = [
  { value: "none", labelKey: "app.profile.disabilityNone", labelDefault: "Ninguna" },
  { value: "sensory", labelKey: "app.profile.disabilitySensory", labelDefault: "Sensorial (visual y auditiva)" },
  { value: "physical", labelKey: "app.profile.disabilityPhysical", labelDefault: "Física" },
  { value: "mental", labelKey: "app.profile.disabilityMental", labelDefault: "Mental" },
  { value: "intellectual", labelKey: "app.profile.disabilityIntellectual", labelDefault: "Intelectual" },
];

export const EMPTY_DEPENDENT = {
  name: "",
  last_name: "",
  dni: "",
  birth_date: "",
  relationship: "",
  health_insurance_name: "",
  health_insurance_number: "",
  // Datos de salud
  height_cm: "",
  weight_kg: "",
  blood_type: "",
  blood_factor: "",
  smoking: "",
  sedentary: "",
  toxics: "",
  medical_antecedents: "",
  // Discapacidad
  disability: "",
  disability_detail: "",
};

// Valores iniciales del formulario a partir de un familiar existente.
// Recorre EMPTY_DEPENDENT para no olvidar campos: si un campo del form no se
// precarga acá, se muestra vacío y al guardar pisa lo que había en la base.
export function toDependentFormValues(dependent) {
  if (!dependent) return EMPTY_DEPENDENT;
  const values = {};
  for (const key of Object.keys(EMPTY_DEPENDENT)) {
    values[key] = dependent[key] ?? "";
  }
  return values;
}

// Formulario de alta/edición de un familiar. Autónomo (maneja su react-hook-form).
export default function DependentForm({
  defaultValues = EMPTY_DEPENDENT,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const { t } = useBrandTranslation();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues });
  const healthInsuranceName = watch("health_insurance_name");

  const disability = watch("disability");
  const hasDisability = disability && disability !== "none";
  const selectClass =
    "w-full h-12 rounded border border-gray-100 bg-white px-2 text-sm-special";

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
      <DialogBody className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="dep-name">{t("app.dependents.name", "Nombre *")}</Label>
        <Input
          id="dep-name"
          intent={errors.name ? "danger" : "default"}
          {...register("name", { required: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-last-name">{t("app.dependents.lastName", "Apellido *")}</Label>
        <Input
          id="dep-last-name"
          intent={errors.last_name ? "danger" : "default"}
          {...register("last_name", { required: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-dni">{t("app.dependents.dni", "DNI")}</Label>
        <Input id="dep-dni" {...register("dni")} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-birth-date">
          {t("app.dependents.birthDate", "Fecha de nacimiento")}
        </Label>
        <Input id="dep-birth-date" type="date" {...register("birth_date")} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-relationship">
          {t("app.dependents.relationship", "Vínculo")}
        </Label>
        <select
          id="dep-relationship"
          className="w-full h-12 rounded border border-gray-100 bg-white px-2 text-sm-special"
          {...register("relationship")}
        >
          <option value="">{t("app.dependents.relSelect", "Seleccionar…")}</option>
          {RELATIONSHIP_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey, opt.labelDefault)}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-hi-name">
          {t("app.dependents.healthInsurance", "Obra social")}
        </Label>
        <HealthInsuranceSelect
          id="dep-hi-name"
          value={healthInsuranceName || ""}
          onChange={(name) =>
            setValue("health_insurance_name", name, { shouldDirty: true })
          }
          className="h-12 border-gray-100"
        />
        <input type="hidden" {...register("health_insurance_name")} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="dep-hi-number">
          {t("app.dependents.healthInsuranceNumber", "N° de afiliado")}
        </Label>
        <Input id="dep-hi-number" {...register("health_insurance_number")} />
      </div>

      {/* Datos de salud */}
      <div className="pt-2">
        <p className="text-sm font-semibold text-gray-800 mb-2">
          {t("app.profile.healthData", "Datos de salud")}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="dep-height">{t("app.profile.height", "Altura (cm)")}</Label>
            <Input
              id="dep-height"
              type="number"
              min="0"
              step="0.1"
              placeholder={t("app.profile.phHeight", "Ej: 170")}
              {...register("height_cm")}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dep-weight">{t("app.profile.weight", "Peso (kg)")}</Label>
            <Input
              id="dep-weight"
              type="number"
              min="0"
              step="0.1"
              placeholder={t("app.profile.phWeight", "Ej: 75")}
              {...register("weight_kg")}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dep-blood-type">{t("app.profile.bloodType", "Grupo sanguíneo")}</Label>
            <select id="dep-blood-type" className={selectClass} {...register("blood_type")}>
              <option value="">{t("app.profile.select", "Seleccionar")}</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="AB">AB</option>
              <option value="O">O</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="dep-blood-factor">{t("app.profile.bloodFactor", "Factor")}</Label>
            <select id="dep-blood-factor" className={selectClass} {...register("blood_factor")}>
              <option value="">{t("app.profile.select", "Seleccionar")}</option>
              <option value="+">Rh+</option>
              <option value="-">Rh-</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="dep-smoking">{t("app.profile.smoking", "Tabaquismo")}</Label>
            <select id="dep-smoking" className={selectClass} {...register("smoking")}>
              <option value="">{t("app.profile.select", "Seleccionar")}</option>
              <option value="yes">{t("app.profile.yes", "Sí")}</option>
              <option value="no">{t("app.profile.no", "No")}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="dep-sedentary">{t("app.profile.sedentary", "Sedentarismo")}</Label>
            <select id="dep-sedentary" className={selectClass} {...register("sedentary")}>
              <option value="">{t("app.profile.select", "Seleccionar")}</option>
              <option value="yes">{t("app.profile.yes", "Sí")}</option>
              <option value="no">{t("app.profile.no", "No")}</option>
            </select>
          </div>
          <div className="space-y-1 col-span-2">
            <Label htmlFor="dep-toxics">{t("app.profile.toxics", "Tóxicos")}</Label>
            <select id="dep-toxics" className={selectClass} {...register("toxics")}>
              <option value="">{t("app.profile.select", "Seleccionar")}</option>
              <option value="yes">{t("app.profile.yes", "Sí")}</option>
              <option value="no">{t("app.profile.no", "No")}</option>
            </select>
          </div>
        </div>
        <div className="space-y-1 mt-3">
          <Label htmlFor="dep-antecedents">{t("app.profile.antecedents", "Antecedentes")}</Label>
          <Textarea
            id="dep-antecedents"
            rows={3}
            placeholder={t(
              "app.profile.antecedentsPlaceholder",
              "Intervenciones quirúrgicas, medicación permanente u otro dato relevante para tu médico"
            )}
            {...register("medical_antecedents")}
          />
        </div>
      </div>

      {/* Discapacidad */}
      <div className="pt-2">
        <p className="text-sm font-semibold text-gray-800 mb-2">
          {t("app.profile.disability", "Discapacidad")}
        </p>
        <div className="space-y-1">
          <Label htmlFor="dep-disability">{t("app.profile.disabilityType", "Tipo")}</Label>
          <select id="dep-disability" className={selectClass} {...register("disability")}>
            {DEPENDENT_DISABILITY_OPTIONS.map(({ value, labelKey, labelDefault }) => (
              <option key={value} value={value}>
                {t(labelKey, labelDefault)}
              </option>
            ))}
          </select>
        </div>
        {hasDisability && (
          <div className="space-y-1 mt-3">
            <Label htmlFor="dep-disability-detail">
              {t("app.profile.disabilityDetail", "Por favor detalle aquí:")}
            </Label>
            <Textarea
              id="dep-disability-detail"
              rows={3}
              placeholder={t("app.profile.disabilityDetailPlaceholder", "Describa su situación")}
              {...register("disability_detail")}
            />
          </div>
        )}
      </div>
      </DialogBody>

      <DialogFooter>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            {t("common.cancel", "Cancelar")}
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? t("common.saving", "Guardando…") : t("common.save", "Guardar")}
        </Button>
      </DialogFooter>
    </form>
  );
}

// Etiqueta legible de un vínculo (para listados).
export function relationshipLabel(value, t) {
  const opt = RELATIONSHIP_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.labelKey, opt.labelDefault) : value || "";
}
